import { Search, X, Building2, FileText, MapPin } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetMySellerQuery } from '../../../app/services/authApi';
import { useSelector } from 'react-redux';
import Card from '../../../Components/Ui/Card';
import DocumentViewer from '../../../Components/Ui/DocumentViewer';
import useDebounce from '../../../hooks/useDebounce';
import Pagination from '../../../Components/Ui/Pagination';
import Loader from '../../../Components/Ui/Loader';

const Distributor = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const limit = 50;
  const navigate = useNavigate();
  const { brandId } = useSelector((state) => state.myBrand);
  const { user: { role: roles = [] } = {} } = useSelector((state) => state.userInfo);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const isBrandAndSeller = roles.includes('brand') && roles.includes('seller');

  // RTK Query hook to fetch sellers
  const { data, isLoading } = useGetMySellerQuery({
    id: brandId,
    page,
    limit,
    search: debouncedSearchQuery,
  });

  const sellers = data?.data || [];
  const totalPages = data?.totalPages || 1;

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 ">
      {/* Header and Search */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 ">Registered Sellers</h2>

        {!isBrandAndSeller && (
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by trade name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10 w-full rounded-lg border border-gray-300 bg-white py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {searchQuery && (
              <X
                className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 cursor-pointer hover:text-gray-700"
                onClick={() => setSearchQuery('')}
              />
            )}
          </div>
        )}
      </div>

      {/* Seller Cards */}
      {sellers.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sellers.map((seller) => (
            <Card
              key={seller._id}
              onClick={() => navigate(`distributor/${seller._id}`)}
              className="cursor-pointer hover:shadow-xl transition-shadow duration-300  secondary-background rounded-lg border border-color"
            >
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Seller Information */}
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <Building2 className="h-5 w-5 text-primary mr-3" />
                      <h3 className="text-lg font-semibold text-gray-800">
                        Trade Name & Owner Name
                      </h3>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-primary mb-1">
                        Trade Name
                      </label>
                      <p className="text-gray-900 font-medium">{seller.tradeName || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-primary mb-1">
                        Owner Name
                      </label>
                      <p className="text-gray-900">{seller.name || 'N/A'}</p>
                    </div>
                  </div>

                  {/* GSTIN & Address */}
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-primary mr-3" />
                      <h3 className="text-lg font-semibold text-gray-800">GSTIN & Address</h3>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-primary mb-1">
                        GST Number
                      </label>
                      <p className="text-gray-900 font-mono bg-gray-50 p-2 rounded border">
                        {seller.gstNumber || 'N/A'}
                      </p>
                    </div>
                    {seller.gstCertificate && (
                      <div>
                        <label className="block text-sm font-medium text-primary mb-1">
                          GST Certificate
                        </label>
                        <DocumentViewer
                          fileUrl={seller.gstCertificate}
                          buttonText="View Certificate"
                        />
                      </div>
                    )}
                    {/* <div className="flex items-center text-sm text-gray-700">
                      <Mail className="h-4 w-4 mr-2 text-primary" />
                      <span>{seller.email}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-700">
                      <Phone className="h-4 w-4 mr-2 text-primary" />
                      <span>{seller.phone || 'N/A'}</span>
                    </div> */}
                    <div className="flex items-start text-sm text-gray-700">
                      <MapPin className="h-4 w-4 mr-2 mt-1 text-primary flex-shrink-0" />
                      <span className="">
                        {seller.primaryAddress
                          ? `${seller.primaryAddress.line1}, ${seller.primaryAddress.line2}, ${seller.primaryAddress.city}, ${seller.primaryAddress.state}, ${seller.primaryAddress.zipCode}`
                          : 'No address available'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-500 mt-16">
          <h3 className="text-xl font-semibold">No Sellers Found</h3>
          <p>No sellers match your search query.</p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center mt-8">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
      )}
    </div>
  );
};

export default Distributor;
